import { CloseCircleGreyIcon, rocketImg } from "assets";
import { ModalPortal, MODAL_LAYER } from "components/modal-portal";

interface HiringProgressModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Candidates analysed so far — omit when the count isn't known. */
  analyzedCandidates?: number;
  /** Credits the blocked action needs — omit when it isn't known. */
  creditsRequired?: number;
  creditsRemaining?: number;
  onTopupCredit?: () => void;
}

/**
 * Out-of-credits prompt shown mid-flow in the hiring assistant.
 *
 * Every figure is optional: the caller often knows only the balance, so each
 * line renders solely when it has a real number behind it rather than falling
 * back to a placeholder that would read as fact.
 */
const HiringProgressModal = ({
  isOpen,
  onClose,
  analyzedCandidates,
  creditsRequired,
  creditsRemaining = 0,
  onTopupCredit,
}: HiringProgressModalProps) => {
  if (!isOpen) return null;

  const hasProgress = typeof analyzedCandidates === "number" && analyzedCandidates > 0;

  return (
    <ModalPortal>
      <div className="fixed inset-0 flex items-center justify-center" style={{ zIndex: MODAL_LAYER.gate }}>
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/50" onClick={onClose} />

        {/* Modal */}
        <div className="relative bg-white rounded-2xl max-w-lg w-full mx-4 overflow-hidden">
          <button
            onClick={onClose}
            aria-label="Close"
            className="absolute top-6 right-4 z-10 hover:opacity-80 transition-opacity"
          >
            <CloseCircleGreyIcon className="w-8 h-8" />
          </button>

          <div className="p-8 pt-6 mt-6">
            <div className="flex justify-center mb-4">
              <img src={rocketImg} alt="" className="w-28 h-28 object-contain" />
            </div>

            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-[#012169] mb-3">
                {hasProgress ? "You're Hiring Like a Pro!" : "You're out of credits"}
              </h2>
              <p className="text-[#667085]">
                {hasProgress ? (
                  <>
                    You've analyzed{" "}
                    <span className="font-bold text-[#012169]">{analyzedCandidates} candidates</span> and unlocked
                    the power of AI screening. Top up to keep going.
                  </>
                ) : (
                  "Top up your credits to continue, or wait for your weekly credits to refresh."
                )}
              </p>
            </div>

            <div className="bg-[#EFF8FF] rounded-xl p-4 mb-8">
              <ul className="space-y-1 text-sm text-[#667085]">
                {typeof creditsRequired === "number" && creditsRequired > 0 && (
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-[#667085] rounded-full" />
                    This action needs{" "}
                    <span className="font-bold text-[#012169]">{creditsRequired.toLocaleString()}</span> credits
                  </li>
                )}
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-[#667085] rounded-full" />
                  You have <span className="font-bold text-[#012169]">{creditsRemaining.toLocaleString()}</span>{" "}
                  credits remaining
                </li>
              </ul>
            </div>

            <button
              onClick={onTopupCredit}
              className="w-full py-4 rounded-lg font-medium bg-[#012169] text-white hover:bg-[#011557] transition-colors"
            >
              Topup Credit
            </button>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};

export { HiringProgressModal };
